<script lang="ts">
  import './app.css';
  import Bubble from './lib/components/Bubble.svelte';
  import WelcomePopup from './lib/components/WelcomePopup.svelte';
  import Frame from './lib/components/Frame.svelte';
  import { appState, resetState } from './lib/state.svelte';
  import { readVisitorCookie, writeVisitorCookie, clearVisitorCookie } from './lib/cookie';
  import { authenticateWidget, fetchWidgetConfig } from './lib/api';
  import type { Message, Visitor } from './lib/types';
  import { SocketProxy } from './lib/socketProxy';

  let {
    baseUrl = '',
    ioFactory,
    fetchImpl,
  }: {
    baseUrl?: string;
    /** Override for tests / dev — defaults to dynamic `socket.io-client` import. */
    ioFactory?: (url: string, opts: Record<string, unknown>) => any;
    fetchImpl?: typeof fetch;
  } = $props();

  let socketProxy: SocketProxy | null = null;
  let rawSocket: any = null;
  let visitorId = $state('');
  let authError = $state('');
  // When true, the whole widget renders nothing. Set for unrecoverable
  // config problems (disabled widget, missing/invalid public key, origin
  // not allowlisted) so a misconfigured embed is invisible to end users
  // instead of showing a broken bubble.
  let fatalConfigError = $state(false);
  let thinkingTimeout: ReturnType<typeof setTimeout> | null = null;
  const THINKING_SAFETY_TIMEOUT_MS = 60_000;
  // Base URL is a build-time decision: dev builds target a local backend so
  // the widget can be embedded on any localhost dev site (tashimoto's own
  // Vite dev server, a customer's local site, etc.) without runtime
  // hostname sniffing. Prod targets support.tashimoto.com — same host the
  // bundle is served from, so customer CSPs only need one tashimoto entry
  // for both script-src and connect-src + wss:. nginx there proxies
  // /api/ + /socket.io/ to the same backend app.tashimoto.com hits.
  // `VITE_API_BASE_URL` env var overrides the default for either mode.
  const DEFAULT_BASE_URL = import.meta.env.VITE_API_BASE_URL
    ?? (import.meta.env.PROD ? 'https://support.tashimoto.com' : 'http://localhost:5010');
  // `||` (not `??`): the prop default is `''`, which `??` treats as a real
  // value and would leave us with relative URLs that hit the host page's
  // origin — fine for tashimoto's own dev proxy, wrong everywhere else.
  const effectiveBaseUrl = $derived(baseUrl || DEFAULT_BASE_URL);

  // Mobile detection (§5.6 Frame.svelte).
  $effect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(max-width: 640px)');
    const update = () => (appState.isMobile = mq.matches);
    update();
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', update);
      return () => mq.removeEventListener('change', update);
    } else if (typeof (mq as any).addListener === 'function') {
      (mq as any).addListener(update);
      return () => (mq as any).removeListener(update);
    }
  });

  // Welcome popup effect (§5.9).
  $effect(() => {
    const cfg = appState.widgetConfig;
    if (!cfg) return;
    if (cfg.welcome_popup_timeout <= 0) return;
    if (!cfg.welcome_popup_message) return;

    const showTimer = setTimeout(() => {
      if (appState.isMinimized && !appState.welcomePopup.dismissed) {
        appState.welcomePopup.visible = true;
      }
    }, cfg.welcome_popup_timeout * 1000);

    return () => {
      clearTimeout(showTimer);
    };
  });

  // Mount-time flow:
  //   1. GET /widget/config — read-only probe, no visitor created. Validates
  //      that the widget is enabled for this provider and this origin is
  //      allowlisted. On 401/403 → fatalConfigError + unmount (disabled
  //      widget never shows a bubble).
  //   2. If a cookie is present, silently re-authenticate the returning
  //      visitor via POST /authenticate/widget to wire up the socket.
  //   First-time visitors (no cookie) stop after step 1 — the form submit
  //   is their first POST, no useless 400 name_required on every page load.
  let probed = false;
  $effect(() => {
    if (probed) return;
    probed = true;
    void mountProbe();
  });

  async function mountProbe(): Promise<void> {
    try {
      const cfg = await fetchWidgetConfig({
        baseUrl: effectiveBaseUrl,
        publicKey: appState.publicKey,
        fetchImpl,
      });
      appState.widgetConfig = cfg.widget;
    } catch (err: any) {
      if (err?.code === 'invalid_public_key' || err?.code === 'origin_not_allowed') {
        fatalConfigError = true;
        return;
      }
      // Transient (network down, rate limit) — don't fatal-fail; the visitor
      // can still try submitting the form to retry.
      appState.error = err?.code || 'connect_failed';
      return;
    }

    const cookie = readVisitorCookie();
    if (!cookie) return;
    appState.cookieId = cookie;
    await doAuth({ cookie_id: cookie });
  }

  async function doAuth(
    partial: { cookie_id?: string; name?: string; email?: string | null }
  ): Promise<boolean> {
    if (appState.isAuthing) return false;
    appState.isAuthing = true;
    authError = '';
    try {
      const res = await authenticateWidget({
        baseUrl: effectiveBaseUrl,
        fetchImpl,
        body: {
          public_key: appState.publicKey,
          ...(partial.cookie_id ? { cookie_id: partial.cookie_id } : {}),
          ...(partial.name ? { name: partial.name } : {}),
          ...(partial.email ? { email: partial.email } : {}),
        },
      });

      // Refresh cookie on every successful auth.
      appState.cookieId = res.cookie_id;
      writeVisitorCookie(res.cookie_id);

      appState.token = res.token;
      appState.widgetConfig = res.widget;
      appState.visitor = {
        name: res.visitor?.name ?? '',
        email: res.visitor?.email ?? '',
        cleared_at: res.visitor?.cleared_at
          ? new Date(res.visitor.cleared_at as unknown as string)
          : null,
      } satisfies Visitor;
      appState.chat = {
        _id: res.chat_id,
        name: res.widget.name || 'Support',
        provider_id: '',
        type: 'Support',
      };
      visitorId = (res.visitor as any)?._id ?? '';

      await openSocket(res.token);
      return true;
    } catch (err: any) {
      authError =
        err?.code === 'name_required'
          ? 'name_required'
          : err?.code === 'rate_limited'
            ? 'rate_limited'
            : 'connect_failed';
      appState.error = authError;
      // Fatal config errors — the widget is disabled for this provider,
      // the publicKey is invalid, or the current origin isn't on the
      // allowlist. No retry from the visitor's side can fix this. Tear
      // everything down so the embed is invisible instead of showing a
      // broken, non-functional bubble. The server returns
      // `invalid_public_key` for both a missing provider and a disabled
      // `support_widget.enabled` — both are equally terminal here.
      if (err?.code === 'invalid_public_key' || err?.code === 'origin_not_allowed') {
        fatalConfigError = true;
        try { rawSocket?.disconnect?.(); } catch { /* noop */ }
        socketProxy = null;
        rawSocket = null;
        return false;
      }
      // If a cookie-based attempt just failed with a non-fatal error, the
      // cookie is likely stale (visitor belonged to a provider that no
      // longer has them — key rotated, provider switched). Drop it so the
      // next session (via VisitorForm submit) starts clean.
      if (partial.cookie_id) {
        clearVisitorCookie();
        appState.cookieId = null;
      }
      return false;
    } finally {
      appState.isAuthing = false;
    }
  }

  // Visitor id cannot be extracted from the JWT — plugins/Jwt.js wraps the
  // payload in Cryptr encryption, so only the server can read it. We read
  // the id from the REST response's `visitor._id` and refresh it from the
  // WS `visitor_authenticate` ack (`data.profile._id`) for belt-and-braces
  // in case of config/schema drift.

  async function openSocket(token: string): Promise<void> {
    if (!ioFactory) {
      try {
        const mod: any = await import('socket.io-client');
        ioFactory = mod.io ?? mod.default?.io ?? mod.default;
      } catch {
        // Without socket.io-client available (shouldn't happen in prod build),
        // we bail out — REST auth still succeeded, widget chrome renders.
        return;
      }
    }

    rawSocket = ioFactory!(effectiveBaseUrl, {
      secure: effectiveBaseUrl.startsWith('https'),
      reconnection: false,
      auth: { token },
    });
    socketProxy = new SocketProxy(rawSocket);

    socketProxy.on('connect', () => {
      appState.isConnected = true;
      // Server's WS wrapper requires a callback on every event — without it,
      // the registered handler silently early-returns and socket.authenticated
      // never flips to true, so every subsequent event would be rejected.
      socketProxy!.emit('visitor_authenticate', token, (err: any, data: any) => {
        if (err) {
          appState.error = 'connect_failed';
          return;
        }
        if (data && typeof data === 'object') {
          if (Array.isArray(data.messages)) {
            // Server orders messages created_at DESC; render oldest-first.
            appState.messages = data.messages.slice().reverse();
          }
          if (data.widget_config) appState.widgetConfig = data.widget_config;
          if (data.profile) {
            appState.visitor.name = data.profile.name ?? appState.visitor.name;
            appState.visitor.email = data.profile.email ?? appState.visitor.email;
            appState.visitor.cleared_at = data.profile.cleared_at
              ? new Date(data.profile.cleared_at)
              : null;
            if (typeof data.profile._id === 'string' && data.profile._id) {
              visitorId = data.profile._id;
            }
          }
          // Initialize ai_paused_by from the chat so reconnects land in the
          // correct state and the next send_message doesn't wrongly show
          // "AI thinking…" while a human is handling.
          if (data.chat && typeof data.chat === 'object') {
            appState.aiPausedBy = (data.chat.ai_paused_by as string | null) ?? null;
          }
        }
      });
    });
    socketProxy.on('disconnect', () => {
      appState.isConnected = false;
    });
    socketProxy.on('message', (msg: Message) => {
      appState.messages = [...appState.messages, msg];
      if (appState.isMinimized) appState.unreadCount++;
      // Any non-visitor message (AI, Agent, System, Bot) clears the
      // "AI thinking…" indicator. The visitor's own echo shouldn't clear it.
      const isOwnEcho =
        msg.source === 'Guest' && msg.created_by === visitorId;
      if (!isOwnEcho) {
        appState.aiThinking = false;
        if (thinkingTimeout) {
          clearTimeout(thinkingTimeout);
          thinkingTimeout = null;
        }
      }
    });
    socketProxy.on(
      'messages_cleared',
      (payload: { chat_id: string; cleared_at: string }) => {
        appState.visitor.cleared_at = new Date(payload.cleared_at);
        appState.messages = [];
      }
    );
    socketProxy.on('visitor_info_updated', (v: Partial<Visitor>) => {
      if (typeof v?.name === 'string') appState.visitor.name = v.name;
      if (typeof v?.email === 'string' || v?.email === null)
        appState.visitor.email = (v.email as string) ?? '';
    });
    socketProxy.on('widget_config', (cfg: any) => {
      if (cfg && typeof cfg === 'object') appState.widgetConfig = cfg;
    });
    // AI pause / resume — drives the "AI Thinking…" suppression when a
    // human has taken over (or the AI has escalated and is waiting for one).
    socketProxy.on('ai_status_changed', (payload: { chat_id: string; ai_paused_by: string | null }) => {
      if (!payload || typeof payload !== 'object') return;
      appState.aiPausedBy = payload.ai_paused_by ?? null;
      if (appState.aiPausedBy && appState.aiThinking) {
        // If the indicator was showing and we've escalated/paused, clear it.
        appState.aiThinking = false;
        if (thinkingTimeout) {
          clearTimeout(thinkingTimeout);
          thinkingTimeout = null;
        }
      }
    });
    // Initial escalation event — arrives alongside the first "Human support
    // requested" system message. Belt-and-braces in case the ai_status_changed
    // emit races the message fan-out.
    socketProxy.on('human_support_requested', () => {
      appState.aiPausedBy = 'system';
      appState.aiThinking = false;
      if (thinkingTimeout) {
        clearTimeout(thinkingTimeout);
        thinkingTimeout = null;
      }
    });
  }

  function openWidget() {
    appState.isMinimized = false;
    appState.welcomePopup.visible = false;
    appState.unreadCount = 0;
  }

  function minimizeWidget() {
    appState.isMinimized = true;
  }

  async function handleVisitorFormSubmit(p: { name: string; email: string | null }) {
    // Clear prior error so the form's submit button is enabled and the
    // error message disappears. The one-shot probe effect doesn't re-fire
    // on this change (its `probed` guard is already true).
    authError = '';
    const ok = await doAuth({
      cookie_id: appState.cookieId ?? undefined,
      name: p.name,
      email: p.email ?? undefined,
    });
    if (ok) openWidget();
  }

  function handleSendMessage(text: string) {
    if (!socketProxy || !appState.chat) return;

    // Only show "AI thinking…" when the AI is actually going to respond.
    // Three conditions must hold:
    //   - the provider has the support-widget AI enabled (ai_enabled),
    //   - the chat isn't paused (escalated to a human or agent takeover),
    //   - the widget config has loaded.
    // Otherwise the indicator would hang until the 60s safety timeout for
    // the AI-disabled case, or be misleading mid-takeover.
    const aiWillRespond =
      !!appState.widgetConfig?.ai_enabled && !appState.aiPausedBy;
    if (aiWillRespond) {
      // Optimistic indicator. The `message` event listener clears it on the
      // next non-self message (AI reply, agent, or system). Safety timer
      // hides it if nothing comes back within a minute.
      appState.aiThinking = true;
      if (thinkingTimeout) clearTimeout(thinkingTimeout);
      thinkingTimeout = setTimeout(() => {
        appState.aiThinking = false;
        thinkingTimeout = null;
      }, THINKING_SAFETY_TIMEOUT_MS);
    }

    // Server signature: socket.on('send_message', (type, msg, chat_id, cb)).
    // Positional args, not a payload object. Callback is required.
    socketProxy.emit('send_message', 'message', text, appState.chat._id, (err: any, info: any) => {
      if (err) {
        appState.aiThinking = false;
        if (thinkingTimeout) {
          clearTimeout(thinkingTimeout);
          thinkingTimeout = null;
        }
        appState.error = typeof info?.message === 'string' ? info.message : 'send_failed';
      }
    });
  }

  // Multi-file visitor upload.
  //
  // Flow per file:
  //   1. Size-check against widget.max_upload_mb (fail-fast before any request).
  //   2. WS `file_upload_setup(chat_id, cb)` → short-lived JWT scoped to this
  //      socket + chat + visitor.
  //   3. POST multipart to /api/v1/upload?token=… with the actual file bytes.
  //   4. Server uploads to S3, creates a `file` Message, broadcasts to the
  //      chat room via queue-message. The widget's existing `message` event
  //      listener picks it up and appends. No further client action needed.
  //
  // Concurrent uploads are allowed. Each is tracked in `appState.uploads`
  // so the composer can render per-file progress bars.
  async function handleUpload(files: File[]) {
    if (!socketProxy || !appState.chat) return;
    if (!appState.widgetConfig?.allow_upload) return;
    const maxMb = appState.widgetConfig?.max_upload_mb || 10;
    const maxBytes = maxMb * 1024 * 1024;
    for (const file of files) {
      const id = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2);
      const item = {
        id,
        name: file.name,
        size: file.size,
        progress: 0,
        status: 'pending' as const,
        error: null as string | null,
      };
      appState.uploads = [...appState.uploads, item];

      if (file.size > maxBytes) {
        updateUpload(id, { status: 'error', error: 'upload_413' });
        continue;
      }

      // Intentionally NOT awaited — concurrent per-file uploads.
      void uploadOne(file, id);
    }
  }

  function updateUpload(id: string, patch: Partial<{ progress: number; status: 'pending' | 'uploading' | 'done' | 'error'; error: string | null }>): void {
    appState.uploads = appState.uploads.map((u) =>
      u.id === id ? { ...u, ...patch } : u
    );
    if (patch.status === 'done') {
      // Leave the `done` entry in state briefly so the progress bar
      // completes visually, then drop it from the render.
      setTimeout(() => {
        appState.uploads = appState.uploads.filter((u) => u.id !== id);
      }, 1500);
    }
  }

  async function uploadOne(file: File, id: string): Promise<void> {
    try {
      updateUpload(id, { status: 'uploading' });
      const chatId = appState.chat!._id;
      const token = await new Promise<string>((resolve, reject) => {
        socketProxy!.emit('file_upload_setup', chatId, (err: any, data: any) => {
          if (err) reject(new Error(data?.message || 'setup_failed'));
          else resolve(data.token);
        });
      });

      const form = new FormData();
      form.append('file', file);

      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${effectiveBaseUrl}/api/v1/upload?token=${encodeURIComponent(token)}`);
        xhr.upload.onprogress = (evt) => {
          if (evt.lengthComputable) {
            updateUpload(id, { progress: evt.loaded / evt.total });
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            // 413 size, 415 type, 400 bad request, 500 server error, etc.
            reject(new Error('upload_' + xhr.status));
          }
        };
        xhr.onerror = () => reject(new Error('upload_network'));
        xhr.send(form);
      });
      updateUpload(id, { progress: 1, status: 'done' });
      // The server-side broadcast appends the file Message to the thread
      // via the existing `message` listener — nothing else to do here.
    } catch (err: any) {
      const code = typeof err?.message === 'string' ? err.message : 'upload_failed';
      updateUpload(id, { status: 'error', error: code });
    }
  }

  function handleClearConfirmed() {
    if (!socketProxy || !appState.chat) return;
    socketProxy.emit('clear_support_chat', { chat_id: appState.chat._id }, (err: any) => {
      if (err) appState.error = 'clear_failed';
    });
  }

  function handleEditProfileSave(p: { name: string; email: string | null }) {
    // Optimistic local update so UI feels instant; server will echo.
    appState.visitor.name = p.name;
    appState.visitor.email = p.email ?? '';
    if (!socketProxy) return;
    socketProxy.emit(
      'update_visitor_info',
      { name: p.name, email: p.email },
      (err: any) => {
        if (err) appState.error = 'update_failed';
      }
    );
  }

  // Exposed for TashimotoSupport.destroy().
  export function shutdown(): void {
    try {
      rawSocket?.disconnect?.();
    } catch {
      // noop
    }
    socketProxy = null;
    rawSocket = null;
    resetState();
  }
</script>

{#if fatalConfigError}
  <!--
    Misconfigured embed (invalid publicKey or disallowed origin). Intentionally
    render nothing — a broken bubble is worse than an absent one.
  -->
{:else if appState.isMinimized}
  <Bubble onClick={openWidget} />
  <WelcomePopup onOpen={openWidget} />
{:else}
  <Frame
    hasVisitor={Boolean(appState.visitor.name)}
    visitorId={visitorId}
    pendingAuth={appState.isAuthing}
    authError={authError}
    onMinimize={minimizeWidget}
    onVisitorFormSubmit={handleVisitorFormSubmit}
    onSendMessage={handleSendMessage}
    onUpload={handleUpload}
    onClearConfirmed={handleClearConfirmed}
    onEditProfileSave={handleEditProfileSave}
  />
{/if}
